import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { FunctionComponent, SetStateAction, useContext } from 'react';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons';
import moment from 'moment';
import { EditUploadLineProjectDialog } from '../../../dialogs/settings/data-import/edit-upload-line-project-dialog';
import { IEditFileUploadLineProject, IFileUploadLineProject } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { useNavigate } from 'react-router-dom';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { Chip, Tooltip, IconButton, Stack } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';

export const CsvUploadProjects: FunctionComponent<{
  fileUploadLineProjects: IFileUploadLineProject[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, uploadLine: IFileUploadLineProject) => void,
  deleteHandler: (id: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLineProject | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineProject) => void,
  status: 'pending' | 'validated' | 'completed' | 'discarded',
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  errorMessage: string,
  errorCode: number,
}> = ({
  fileUploadLineProjects,
  editClickHandler,
  deleteHandler,
  openDialog,
  setValidatedUploadLines,
  selected,
  setSelected,
  editUploadLine,
  closeDialogHandler,
  submitHandler,
  status,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  setOrder,
  setOrderBy,
  isSubmitting,
  errorMessage,
  errorCode,
}) => {
    const navigate = useNavigate();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = Array.isArray(uploadLine.validationErrors) ? uploadLine.validationErrors : []

        const fieldList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''

        const errorList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''

        const severityList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const headCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...(status !== 'discarded' ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'summary', numeric: false, label: screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' }) + ' Summary', sortable: true },
        { id: 'projectReference', numeric: false, label: screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' }) + ' Reference', sortable: true },
        { id: 'description', numeric: false, label: screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' }) + ' Description', sortable: true },
        { id: 'startDate', numeric: false, label: 'Start Date', sortable: true },
        { id: 'endDate', numeric: false, label: 'End Date', sortable: true },
        { id: 'assignedUserName', numeric: false, label: 'Assigned User', sortable: true },
      ];

    const rows = status === 'pending'
      ? mapValidationErrors(fileUploadLineProjects || [])
      : fileUploadLineProjects?.map((uploadLine: any) => ({
        id: uploadLine.id,
        summary: uploadLine.summary,
        projectReference: uploadLine.projectReference,
        description: uploadLine.description,
        startDate: uploadLine.startDate ? moment(uploadLine.startDate).format('DD/MM/YYYY') : '',
        endDate: uploadLine.endDate ? moment(uploadLine.endDate).format('DD/MM/YYYY') : '',
        assignedUserName: uploadLine.assignedUser ? uploadLine.assignedUser.firstName + ' ' + uploadLine.assignedUser.surname : '',
        action: status === 'validated' ? (
          <TableActionButtons
            id={uploadLine.id}
            includeEdit={true}
            includeDelete={true}
            onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
            onDelete={(id: string) => deleteHandler(id)}
          />
        ) : status === 'completed' ? (
          <TableActionButtons
            id={uploadLine.id}
            includeView={true}
            onView={() => viewProjectClickHandler(uploadLine.projectId)}
            disableButton={!uploadLine.projectId}
          />
        ) : undefined,
      }));

    const viewProjectClickHandler = (projectId?: string) => {
      if (projectId) {
        navigate(`/organisation/projects/${projectId}`);
      }
    };

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => ({ id: row }));
      setValidatedUploadLines(formatted)
    };

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc') => {
      setOrderBy(orderBy);
      setOrder(order);
    };

    return (
      <>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selectByIcon={(status === 'validated' || status === 'pending') ? false : true}
          selectByCheckboxOnly={(status === 'validated' || status === 'pending') ? true : false}
          onOrderChange={handleOrderChange}
          handleSelected={handleSelected}
          haveCheckboxSelect={(status === 'validated' || status === 'pending') ? true : false}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
        />
        {editUploadLine && openDialog &&
          <EditUploadLineProjectDialog
            open={openDialog}
            errorMessage={errorMessage || ''}
            errorCode={errorCode || 0}
            isSubmitting={isSubmitting}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine as IFileUploadLineProject}
          />
        }
      </>
    );
  }


